import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { removeUndefinedProperties } from '@attain-apps/object-utils'

import type { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /* eslint-disable sort-keys */
    deploy: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        cancellation: ({ state }) => ({
          ...state,
          deploymentError: undefined,
          isDeploying: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          deploymentError: error,
          isDeploying: false,
        }),
        request: ({ state }) => ({
          ...state,
          deploymentError: undefined,
          isDeploying: true,
        }),
        success: ({ state }) => ({
          ...state,
          deploymentError: undefined,
          isDeploying: false,
          variableValues: {},
        }),
      },
    }),
    evaluate: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<{
        readonly evaluation: ents.core.quiver.Evaluation
      }>(),
      reducers: {
        cancellation: ({ state }) => ({
          ...state,
          evaluationError: undefined,
          isEvaluating: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          evaluationError: error,
          isEvaluating: false,
        }),
        request: ({ state }) => ({
          ...state,
          evaluationError: undefined,
          isEvaluating: true,
        }),
        success: ({
          state,
          data: {
            response: { evaluation },
          },
        }) => ({
          ...state,
          evaluationChain: [...state.evaluationChain, evaluation],
          evaluationError: undefined,
          isEvaluating: false,
          variableValues: {
            ...evaluation.requires?.reduce(
              (prev, variable) =>
                Object.assign(prev, {
                  [variable.id]: variable.defaultValue,
                }),
              {},
            ),
            ...removeUndefinedProperties({ ...state.variableValues }),
          },
        }),
      },
    }),
    putVariableValues: store.actions.standalone({
      data: store.actions.Data.asIs<ents.core.dms.data.query.Variable.Lookup>(),
      reducer: ({ state, data }) => ({
        ...state,
        variableValues: {
          ...state.variableValues,
          ...data,
        },
      }),
    }),
    undoLastEvaluation: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        evaluationChain: state.evaluationChain.slice(0, -1),
      }),
    }),
    clearEvaluations: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        evaluationChain: [],
        variableValues: {},
      }),
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State>
}
